import { bootstrapApplication } from '@angular/platform-browser';
import { ConfigurationService } from '@processpuzzle/util';
import { KeycloakAuthConfig } from '@processpuzzle/auth/domain';
import { environment as buildTimeEnv } from './environments/environment';
import { EnvironmentVariables } from './environments/environment-variables';
import { AppComponent } from './app/app.component';
import { createAppConfig } from './app/app.config';
import { RuntimeConfiguration } from './app/runtime-configuration';

/**
 * The container's rendered environment, or the build-time one when the file is not there.
 *
 * `assets/runtime-env.json` is written by the Docker entrypoint from `envsubst`, which is what lets
 * one image serve every stage. `cache: 'no-store'` because a cached copy would pin the first stage
 * the browser ever saw.
 */
async function loadEnvironment(): Promise<EnvironmentVariables> {
  try {
    const response = await fetch('assets/runtime-env.json', { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return (await response.json()) as EnvironmentVariables;
  } catch (error) {
    console.warn('runtime-env.json not available, using build-time environment', error);
    return buildTimeEnv as EnvironmentVariables;
  }
}

/**
 * Points authentication at the Keycloak origin this deployment actually runs, when the environment
 * names one.
 *
 * `run-time-conf/config.*.json` is baked into the image, and one image serves CI, local compose and
 * production. Those three share `PIPELINE_STAGE=prod` and therefore one configuration file, but they
 * do not share a Keycloak hostname, so the origin has to come from the environment; the file value
 * remains the fallback for every stage that needs no override.
 *
 * **The value must be Keycloak's own origin, never a path prefix of this one.** Keycloak scopes
 * `KEYCLOAK_SESSION` and `KEYCLOAK_IDENTITY` to `Path=/realms/<realm>/`, so an `/auth` proxy on this
 * origin puts every Keycloak page on a path those cookies do not match. The browser then withholds
 * them and two things fail silently: the login-status iframe reads no `KEYCLOAK_SESSION`, answers
 * `"changed"`, and keycloak-js clears the tokens it obtained seconds earlier — the next API call
 * refreshes with an undefined token, Keycloak logs `REFRESH_TOKEN_ERROR … "Invalid refresh token"`
 * and the request comes back 401 — while `prompt=none` returns `login_required`, so no session
 * survives a reload either. Both were observed on the local stack; neither is visible in a
 * healthcheck.
 *
 * Only `keycloak` configurations are touched.
 */
function withKeycloakUrl(env: EnvironmentVariables, runtimeConfiguration: RuntimeConfiguration): RuntimeConfiguration {
  const keycloakUrl = env.KEYCLOAK_URL?.trim();
  const authenticationConfiguration = runtimeConfiguration.AUTHENTICATION_CONFIGURATION;
  if (!keycloakUrl || authenticationConfiguration.AUTHENTICATION_PROVIDER !== 'keycloak') return runtimeConfiguration;

  const authServiceConfig = authenticationConfiguration.AUTH_SERVICE_CONFIG as KeycloakAuthConfig | undefined;
  return {
    ...runtimeConfiguration,
    AUTHENTICATION_CONFIGURATION: {
      ...authenticationConfiguration,
      AUTHENTICATION_SERVICE_ROOT: keycloakUrl,
      ...(authServiceConfig ? { AUTH_SERVICE_CONFIG: { ...authServiceConfig, authServerUrl: keycloakUrl } } : {}),
    },
  };
}

/**
 * No realm resolution here, unlike `processpuzzle-biz-frontend`.
 *
 * This application administers every tenant, so its users are staff of the platform and belong to
 * the fixed `processpuzzle-admin` realm named in `run-time-conf/config.common.json`. Reading a
 * realm out of the URL would be actively wrong: the first path segment of `/organization/acme` is a
 * screen, not a tenant.
 */
async function bootstrap() {
  const env = await loadEnvironment();
  const configurationService = new ConfigurationService<EnvironmentVariables, RuntimeConfiguration>();
  const runtimeConfiguration = (await configurationService.init(env)) as RuntimeConfiguration;

  await bootstrapApplication(AppComponent, createAppConfig(withKeycloakUrl(env, runtimeConfiguration)));
}

bootstrap().catch((error) => console.error(error));
